// TutorialFlow — the in-product spotlight tutorial engine.
//
// Renders a NON-BLOCKING spotlight ring around the element the current step
// explains, plus a tooltip card next to it with the step's title, rich-text body
// and Back / Next / Skip controls. Purely EXPLANATORY: it never mutates data —
// unlike components/guided/GuidedFlow.jsx, which is the do-along wizard that
// drives the assistant chat.
//
// The overlay DOES NOT dim or swallow clicks: the whole page stays usable while
// a tour runs, because the tours tell the user to click real things ("open the
// rule and its Code Editor tab") and a modal backdrop made that impossible. Only
// the card itself takes pointer events, and it can be DRAGGED by its header out
// of the way of whatever it is pointing at.
//
// A tour is a declarative object (see components/tutorial/tours/):
//   { key, title, description, steps: [step, ...] }
// step: {
//   title:      string
//   body:       html string (rendered with dangerouslySetInnerHTML — our copy)
//   page?:      cortex page key ("data-elements", "apps/<view>") — the engine
//               navigates there first (project/branch-prefixed via createPageUrl).
//               MAY be a function (currentPathname) => absolutePath | null for
//               steps whose destination depends on runtime state (e.g. the Flow
//               studio's /flow/projects/<forkId>/… routes); null = stay put.
//   target?:    CSS selector or [selectors] — first match is spotlighted.
//               Omitted / not found within waitMs → centered modal step.
//               Selectors are searched in the host document FIRST, then inside
//               the open app embed ([data-testid="app-view-iframe"], same-origin
//               /flow-embed) so guides can spotlight elements INSIDE an app
//               built in cortex. Prefix with "app:" to search only the embed
//               (e.g. "app:[data-testid=customers-table]"). CROSS-ORIGIN embeds
//               (an APP SERVICE, a deployed portal app) are driven through the
//               cortex embed BRIDGE (shared/embed-bridge/cortex-embed-bridge.js,
//               included in the page): the same selectors travel by postMessage
//               and come back as rects, so a guide can still click the app's
//               sidebar and spotlight what it shows. A page without the bridge
//               falls back to a centered card (2026-09-07: allvue_dq's Allvue
//               DQ became a service and every guide lost its in-app steps).
//   placement?: "auto" | "top" | "bottom" | "left" | "right"   (default auto)
//   waitMs?:    how long to poll for the target (default 6000)
//   appClick?:  selector (queryTarget syntax — "app:text=Data grid",
//               "app:.nav-item") CLICKED inside the app embed on step enter,
//               after page navigation and before target polling. This is how a
//               tour drives a section-based app (sidebar/tab navigation) so
//               the elements the step spotlights actually exist. NAVIGATION
//               ONLY by contract — guides must never appClick action buttons.
//   demo?:      string | { open: string, ... } — a DISPLAY-ONLY instruction the
//               active page fulfils: it dispatches `cortex:tutorial-demo` with
//               this payload on step enter (e.g. { open: "add-member" } opens
//               that modal in a no-submit demo mode so the tour can spotlight
//               its fields). On leaving the step / ending the tour the engine
//               dispatches `cortex:tutorial-demo` with { close: true }. Pages
//               MUST NOT mutate data in demo mode — modals open for show only.
// }
//
// Mounted in Layout (like GuidedFlow) so it survives the route changes its own
// steps make. Started via the `cortex:start-tutorial` window event.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, GraduationCap, X } from "lucide-react";
import { createPageUrl } from "@/utils";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const PAD = 6;          // spotlight padding around the target rect
const TIP_W = 380;      // tooltip width
const MARGIN = 12;      // min distance from viewport edges

export function markTutorialSeen(key) {
  try { localStorage.setItem(`cortex-tutorial-seen:${key}`, "1"); } catch {}
}
export function isTutorialSeen(key) {
  try { return localStorage.getItem(`cortex-tutorial-seen:${key}`) === "1"; } catch { return false; }
}

// The open app embed's document, when reachable. Apps built in cortex render
// in a same-origin /flow-embed iframe, so its DOM is directly queryable;
// deployed portal apps are cross-origin and contentDocument throws/nulls —
// the try/catch turns those into "not found" (centered-card fallback).
function appEmbedDocs() {
  const docs = [];
  // Both app-embed iframes are same-origin: the in-cortex Apps page and the
  // chrome-free share page (which mounts its own TutorialFlow instance).
  // Missing the share one made every in-app target "not found" there — tours
  // degraded to centered cards no matter what the author anchored.
  for (const f of document.querySelectorAll('iframe[data-testid="app-view-iframe"], iframe[data-testid="shared-app-iframe"]')) {
    try { const d = f.contentDocument; if (d && d.body) docs.push(d); } catch {}
  }
  return docs;
}

// Embed iframes whose document is NOT reachable (cross-origin: an app service
// on its own host, a deployed portal app). These are driven through the embed
// bridge the page includes — see remoteQuery.
function remoteEmbedFrames() {
  const out = [];
  for (const f of document.querySelectorAll('iframe[data-testid="app-view-iframe"], iframe[data-testid="shared-app-iframe"]')) {
    let reachable = false;
    try { reachable = !!(f.contentDocument && f.contentDocument.body); } catch { reachable = false; }
    if (!reachable && f.contentWindow) out.push(f);
  }
  return out;
}

// One in-flight table for bridge answers: id → resolve. A single window
// listener serves every TutorialFlow instance (Layout + the share page).
const pendingRemote = new Map();
let remoteSeq = 0;
let remoteListening = false;
function ensureRemoteListener() {
  if (remoteListening) return;
  remoteListening = true;
  window.addEventListener("message", (e) => {
    const d = e.data;
    if (!d || d.source !== "cortex-tutorial-result" || !pendingRemote.has(d.id)) return;
    const { resolve, frame } = pendingRemote.get(d.id);
    pendingRemote.delete(d.id);
    resolve({ found: !!d.found, rect: d.rect || null, frame });
  });
}
// Ask the bridge inside `frame` to resolve (op "query") or click (op "click")
// a selector. Answers within `timeoutMs`, else null (no bridge, page not yet
// loaded, or the page ignores us) — the caller treats that as "not found".
function remoteQuery(frame, selector, op = "query", { scroll = false, timeoutMs = 350 } = {}) {
  ensureRemoteListener();
  return new Promise((resolve) => {
    const id = `t${Date.now().toString(36)}-${++remoteSeq}`;
    const timer = setTimeout(() => { pendingRemote.delete(id); resolve(null); }, timeoutMs);
    pendingRemote.set(id, { frame, resolve: (v) => { clearTimeout(timer); resolve(v); } });
    try { frame.contentWindow.postMessage({ source: "cortex-tutorial", op, id, selector, scroll }, "*"); }
    catch { clearTimeout(timer); pendingRemote.delete(id); resolve(null); }
  });
}
// A remote hit as the engine measures it: the page's own rect, shifted by the
// iframe's position in the host viewport, padded like a local element.
// The part of the element that is actually VISIBLE through the iframe: a row
// wider than the frame, or an element scrolled below its fold, must not put
// the ring (and the card that follows it) outside the frame or the viewport.
// Entirely out of view → null (the caller asks the bridge to scroll it in).
function measureRemote(hit) {
  if (!hit?.rect || (hit.rect.width === 0 && hit.rect.height === 0)) return null;
  const fr = hit.frame.getBoundingClientRect();
  const top = Math.max(hit.rect.top + fr.top, fr.top);
  const left = Math.max(hit.rect.left + fr.left, fr.left);
  const bottom = Math.min(hit.rect.top + hit.rect.height + fr.top, fr.bottom);
  const right = Math.min(hit.rect.left + hit.rect.width + fr.left, fr.right);
  if (bottom - top < 4 || right - left < 4) return null;
  return {
    top: Math.round(top - PAD),
    left: Math.round(left - PAD),
    width: Math.round(right - left + PAD * 2),
    height: Math.round(bottom - top + PAD * 2),
  };
}
const remoteOutOfView = (hit) => !!hit?.remote && measureRemote(hit) === null;
// Resolve a target locally first (host document, same-origin embeds), then
// through the bridge of every cross-origin embed. Returns an Element, a
// remote hit { remote: true, frame, rect }, or null.
async function queryTargetAnywhere(target, { scroll = false } = {}) {
  const local = queryTarget(target);
  if (local) return local;
  const frames = remoteEmbedFrames();
  if (!frames.length) return null;
  const sels = (Array.isArray(target) ? target : [target]).filter((x) => typeof x === "string");
  for (const sel of sels) {
    const inApp = sel.startsWith("app:") ? sel.slice(4).trim() : sel;
    for (const frame of frames) {
      const hit = await remoteQuery(frame, inApp, "query", { scroll });
      if (hit?.found) return { remote: true, frame, rect: hit.rect };
    }
  }
  return null;
}
// Click a selector wherever it lives: a local element, or through the bridge.
async function clickAnywhere(selector) {
  const local = queryTarget(selector);
  if (local) { try { local.click(); } catch {} return true; }
  const inApp = selector.startsWith("app:") ? selector.slice(4).trim() : selector;
  for (const frame of remoteEmbedFrames()) {
    const hit = await remoteQuery(frame, inApp, "click");
    if (hit?.found) return true;
  }
  return false;
}
const measureAny = (hit) => (!hit ? null : hit.remote ? measureRemote(hit) : measure(hit));

// "text=<label>" targets: many app controls (tabs, buttons) carry no id or
// test-id — their only stable identity is their visible label. Match exact
// trimmed text (case-insensitive) on clickable elements, preferring the app's
// own view root over any chrome beneath it.
function queryByText(doc, label) {
  const want = label.trim().toLowerCase();
  if (!want) return null;
  const scopes = [];
  for (const r of doc.querySelectorAll('[data-view-root], [id$="-root"]')) scopes.push(r);
  scopes.push(doc);
  for (const scope of scopes) {
    for (const el of scope.querySelectorAll('button, [role="tab"], [role="button"], a, [role="menuitem"]')) {
      const t = (el.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      if (t === want && (el.offsetWidth || el.offsetHeight)) return el;
    }
    // Leaf fallback: headings, sidebar spans and KPI titles are often plain
    // divs/spans with no role — match visible childless elements by text.
    for (const el of scope.querySelectorAll("*")) {
      if (el.childElementCount !== 0) continue;
      if (!(el.offsetWidth || el.offsetHeight)) continue;
      const t = (el.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      if (t === want) return el;
    }
  }
  // No exact match: the SMALLEST visible element whose text CONTAINS the label
  // (a sentence inside a paragraph, a nav label with a count badge). Same rule
  // as the embed bridge, so a guide anchor behaves alike in a view and a service.
  let best = null; let bestLen = Infinity;
  for (const el of doc.querySelectorAll("*")) {
    if (el === doc.body || el === doc.documentElement || !(el.offsetWidth || el.offsetHeight)) continue;
    const t = (el.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
    if (t.length < bestLen && t.includes(want)) { best = el; bestLen = t.length; }
  }
  return best;
}

// Resolve a step's target element right now (steps re-query every tick so a
// React re-render that swaps DOM nodes can't leave the spotlight on a corpse).
// Host document first, then inside the app embed; an "app:" prefix skips the
// host lookup so a guide can target in-app elements unambiguously. Both sides
// accept "text=<label>" in place of a CSS selector.
function queryTarget(target) {
  if (!target) return null;
  const sels = Array.isArray(target) ? target : [target];
  for (const s of sels) {
    if (typeof s !== "string") continue;
    const inAppOnly = s.startsWith("app:");
    const sel = inAppOnly ? s.slice(4).trim() : s;
    const byText = sel.startsWith("text=") ? sel.slice(5) : null;
    if (!inAppOnly) {
      try {
        const el = byText ? queryByText(document, byText) : document.querySelector(sel);
        if (el) return el;
      } catch {}
    }
    for (const doc of appEmbedDocs()) {
      try {
        const el = byText ? queryByText(doc, byText) : doc.querySelector(sel);
        if (el) return el;
      } catch {}
    }
  }
  return null;
}

function measure(el) {
  const r = el.getBoundingClientRect();
  if (!r || (r.width === 0 && r.height === 0)) return null;
  // An element inside the (same-origin) app embed reports coordinates in the
  // IFRAME's viewport — translate into host-viewport space by adding each
  // ancestor frame's own offset so the spotlight ring lands where the user
  // actually sees the element.
  let top = r.top, left = r.left;
  try {
    let win = el.ownerDocument?.defaultView;
    while (win && win !== window && win.frameElement) {
      const fr = win.frameElement.getBoundingClientRect();
      top += fr.top; left += fr.left;
      win = win.parent;
    }
  } catch {}
  return {
    top: Math.round(top - PAD),
    left: Math.round(left - PAD),
    width: Math.round(r.width + PAD * 2),
    height: Math.round(r.height + PAD * 2),
  };
}

// Pick a tooltip position near the spotlight rect, clamped to the viewport.
// `est` is the card's height — measured from the real DOM node after render
// (a fixed estimate let long-bodied cards overflow the bottom of the screen).
function tipPosition(rect, placement, est) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const clampX = (x) => Math.max(MARGIN, Math.min(x, vw - TIP_W - MARGIN));
  const clampY = (y) => Math.max(MARGIN, Math.min(y, vh - est - MARGIN));
  const candidates = placement && placement !== "auto"
    ? [placement]
    : ["bottom", "top", "right", "left"];
  for (const p of candidates) {
    if (p === "bottom" && rect.top + rect.height + est + MARGIN < vh) {
      return { top: rect.top + rect.height + 10, left: clampX(rect.left) };
    }
    if (p === "top" && rect.top - est - MARGIN > 0) {
      return { top: rect.top - est - 10, left: clampX(rect.left) };
    }
    if (p === "right" && rect.left + rect.width + TIP_W + MARGIN < vw) {
      return { top: clampY(rect.top), left: rect.left + rect.width + 10 };
    }
    if (p === "left" && rect.left - TIP_W - MARGIN > 0) {
      return { top: clampY(rect.top), left: rect.left - TIP_W - 10 };
    }
  }
  // Nothing fits cleanly — put it under the rect, clamped.
  return { top: clampY(rect.top + rect.height + 10), left: clampX(rect.left) };
}

export default function TutorialFlow({ tour, navigate, onClose }) {
  const steps = tour.steps || [];
  const N = steps.length;
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);     // spotlight rect or null = centered
  const [settled, setSettled] = useState(false); // target search finished
  const [cardH, setCardH] = useState(240);    // real card height (measured)
  // User-dragged position. null = follow the automatic placement; once the user
  // drags the card it PINS to {top,left} and stays there for the rest of the
  // tour (steps move the spotlight, not the card the user just parked).
  const [pinned, setPinned] = useState(null);
  const cardRef = useRef(null);
  const dragRef = useRef(null);
  const stepRef = useRef(null);
  stepRef.current = steps[Math.min(idx, N - 1)];
  const step = stepRef.current;

  const finish = useCallback((completed) => {
    markTutorialSeen(tour.key);
    onClose?.(completed);
  }, [tour.key, onClose]);

  const go = useCallback((k) => {
    if (k < 0) return;
    if (k >= N) { finish(true); return; }
    setIdx(k);
  }, [N, finish]);

  // Emit a display-only demo instruction to the active page, and always clear
  // any previously-open demo surface first so steps can't stack modals. The
  // `tutorial-demo-active` <html> class lets demo modals lift their z-index
  // above the spotlight backdrop (see index.css) so the tour can show them.
  const emitDemo = useCallback((demo) => {
    try { window.dispatchEvent(new CustomEvent("cortex:tutorial-demo", { detail: { close: true } })); } catch {}
    if (demo) {
      const detail = typeof demo === "string" ? { open: demo } : demo;
      try { document.documentElement.classList.add("tutorial-demo-active"); } catch {}
      try { window.dispatchEvent(new CustomEvent("cortex:tutorial-demo", { detail })); } catch {}
    } else {
      try { document.documentElement.classList.remove("tutorial-demo-active"); } catch {}
    }
  }, []);

  // Close any demo surface when the tour ends (finish/Esc) or unmounts.
  useEffect(() => () => {
    try { document.documentElement.classList.remove("tutorial-demo-active"); } catch {}
    try { window.dispatchEvent(new CustomEvent("cortex:tutorial-demo", { detail: { close: true } })); } catch {}
  }, []);

  // Per-step: navigate to the step's page, then poll for its target element.
  useEffect(() => {
    let cancelled = false;
    setSettled(false);
    setRect(null);
    (async () => {
      const s = steps[idx];
      if (!s) return;
      if (s.page != null) {
        const url = typeof s.page === "function"
          ? s.page(window.location.pathname)
          : createPageUrl(s.page);
        if (url) {
          // Compare pathname AND query: app steps flip the embed's internal
          // page purely via the query (apps/<view>?page=records → iframe
          // hash), so a same-path different-query step must still navigate.
          const q = url.indexOf("?");
          const want = q === -1 ? url : url.slice(0, q);
          const wantSearch = q === -1 ? "" : url.slice(q);
          if (window.location.pathname !== want || (wantSearch && window.location.search !== wantSearch)) {
            try { navigate(url); } catch {}
          }
        }
      }
      // Demo instruction: open (or clear) a modal for display. Fire AFTER any
      // navigation so the destination page is mounted to receive the event;
      // give it a beat to mount its listener + render the modal before we poll
      // for the target inside it.
      emitDemo(s.demo || null);
      if (s.demo) await sleep(350);
      if (cancelled) return;
      // In-app navigation click + target polling, ONE loop: appClick brings
      // the section this step describes into view (sidebar / tab item inside
      // the embed). Nav clicks are idempotent, so the loop keeps re-clicking
      // (throttled) until the target shows up — a one-shot click raced app
      // renders and left the target step spotlighting nothing.
      const clickSel = s.appClick
        ? (s.appClick.startsWith("app:") ? s.appClick : `app:${s.appClick}`)
        : null;
      if (s.target || clickSel) {
        const cap = s.waitMs ?? (clickSel ? 15000 : 6000);
        const t0 = Date.now();
        let el = null;
        let lastClick = 0;
        let everClicked = false;
        while (!cancelled && Date.now() - t0 < cap) {
          if (clickSel && Date.now() - lastClick > 900) {
            // Same-origin: click the element; cross-origin: the bridge clicks it.
            if (await clickAnywhere(clickSel)) { lastClick = Date.now(); everClicked = true; }
          }
          if (s.target) {
            el = await queryTargetAnywhere(s.target);
            if (el) break;
          } else if (everClicked) {
            break; // click-only step: done as soon as the nav click landed
          }
          await sleep(150);
        }
        if (cancelled) return;
        if (el) {
          if (el.remote) {
            // The bridge scrolls the element into view (instantly) and answers
            // with the post-scroll rect.
            const again = await queryTargetAnywhere(s.target, { scroll: true });
            await sleep(250);
            if (cancelled) return;
            const m = measureAny(again || el);
            if (m) setRect(m);
          } else {
            try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch {}
            await sleep(250); // let the scroll settle before first measure
            if (cancelled) return;
            const m = measure(el);
            if (m) setRect(m);
          }
        }
      }
      if (!cancelled) setSettled(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Keep the spotlight glued to the target through scrolls / layout shifts:
  // re-query + re-measure on an interval while a targeted step is active.
  useEffect(() => {
    const s = steps[idx];
    if (!s?.target) return;
    let busy = false;
    const t = setInterval(async () => {
      if (busy) return; // a bridge round trip may outlast one tick
      busy = true;
      try {
        let hit = await queryTargetAnywhere(s.target);
        // A remote target that drifted out of the frame's view (the app
        // re-rendered, the user scrolled) is scrolled back by the bridge.
        if (remoteOutOfView(hit)) hit = await queryTargetAnywhere(s.target, { scroll: true });
        const m = measureAny(hit);
        setRect((prev) => {
          if (!m) return prev; // keep last known rect while the node re-mounts
          if (prev && Math.abs(prev.top - m.top) < 2 && Math.abs(prev.left - m.left) < 2 &&
              Math.abs(prev.width - m.width) < 2 && Math.abs(prev.height - m.height) < 2) return prev;
          return m;
        });
      } finally { busy = false; }
    }, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Keyboard: ← → navigate, Esc skips out. The page stays fully usable during a
  // tour, so the STEPPING keys must never steal input the user aimed at the app:
  // ←/→/Enter only fire from the page background or the card itself, never while
  // typing, inside an open popover, or with focus parked on an app control those
  // keys belong to. Esc is the tour's escape hatch and stays global (it only
  // defers to an open popover/dropdown, which should close itself first).
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      const popoverOpen = !!document.querySelector("[data-radix-popper-content-wrapper]");
      const inCard = !!t?.closest?.("[data-tutorial-card]");
      const onBackground = t === document.body || t === document.documentElement || t == null;
      const steppable = !typing && !popoverOpen && (inCard || onBackground);
      if (e.key === "Escape") {
        if (popoverOpen) return; // let the dropdown close itself first
        e.preventDefault(); finish(false);
      } else if (e.key === "ArrowRight" || e.key === "Enter") {
        if (!steppable) return;
        e.preventDefault(); go(idx + 1);
      } else if (e.key === "ArrowLeft") {
        if (!steppable) return;
        e.preventDefault(); go(idx - 1);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [idx, go, finish]);

  // Measure the card's real height after every render that can change it, so
  // placement clamping never leaves the controls below the fold.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const h = el.offsetHeight;
    if (h && Math.abs(h - cardH) > 4) setCardH(h);
  });

  // Drag the card by its header. Pointer capture keeps the gesture alive even
  // when the cursor outruns the card; the result is clamped so the card can
  // never be parked off-screen where its controls are unreachable.
  const clampCard = useCallback((top, left) => {
    const el = cardRef.current;
    const w = el?.offsetWidth || TIP_W;
    const h = el?.offsetHeight || cardH;
    return {
      top: Math.max(MARGIN, Math.min(top, window.innerHeight - Math.min(h, window.innerHeight - MARGIN * 2) - MARGIN)),
      left: Math.max(MARGIN, Math.min(left, window.innerWidth - w - MARGIN)),
    };
  }, [cardH]);

  const onDragStart = useCallback((e) => {
    // Only the header itself starts a drag — never the Skip button inside it.
    if (e.button != null && e.button !== 0) return;
    if (e.target?.closest?.("button")) return;
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    setPinned({ top: r.top, left: r.left });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    e.preventDefault();
  }, []);

  const onDragMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    setPinned(clampCard(e.clientY - d.dy, e.clientX - d.dx));
  }, [clampCard]);

  const onDragEnd = useCallback((e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  }, []);

  // Keep a pinned card inside the viewport when the window is resized.
  useEffect(() => {
    if (!pinned) return;
    const onResize = () => setPinned((p) => (p ? clampCard(p.top, p.left) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pinned, clampCard]);

  const centered = settled && !rect;
  const tip = useMemo(
    () => (rect ? tipPosition(rect, step?.placement, cardH + 8) : null),
    [rect, step, cardH]
  );

  if (!step) return null;

  // The overlay NEVER takes pointer events: the page underneath stays fully
  // usable so the user can actually do what the step describes. Only the card
  // opts back in.
  const cardPos = pinned
    ? { top: pinned.top, left: pinned.left, width: rect && tip ? TIP_W : 460 }
    : (rect && tip
      ? { top: tip.top, left: tip.left, width: TIP_W, transition: "top .3s ease, left .3s ease" }
      : { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 460 });
  return (
    <div
      className="fixed inset-0"
      style={{ zIndex: 100000, pointerEvents: "none" }}
      data-tutorial-overlay
      data-tutorial-step={idx}
      data-tutorial-settled={settled ? "1" : "0"}
    >
      {/* Spotlight ring — a highlight around the target that marks it without
          dimming or covering the page. No backdrop: nothing here blocks clicks. */}
      {rect && (
        <div
          data-tutorial-spotlight
          className="absolute rounded-[10px] pointer-events-none"
          style={{
            top: rect.top, left: rect.left, width: rect.width, height: rect.height,
            boxShadow: "0 0 0 2px rgba(59,130,246,0.95), 0 0 0 6px rgba(59,130,246,0.22), 0 0 18px 4px rgba(59,130,246,0.35)",
            transition: "top .3s ease, left .3s ease, width .3s ease, height .3s ease",
          }}
        />
      )}

      {/* Tooltip / card — the only interactive part of the overlay. Draggable
          by its header so it can be moved off whatever it points at. */}
      <div
        ref={cardRef}
        data-tutorial-card
        data-tutorial-pinned={pinned ? "1" : undefined}
        className="absolute rounded-[10px] bg-card-bg text-page-text shadow-2xl border border-page-divider overflow-hidden select-none"
        style={{ pointerEvents: "auto", maxWidth: "calc(100vw - 24px)", ...cardPos }}
      >
        {/* progress hairline */}
        <div className="h-1 bg-page-bg">
          <div className="h-1 bg-nav-active transition-all duration-300" style={{ width: `${((idx + 1) / N) * 100}%` }} />
        </div>
        <div
          data-tutorial-drag-handle
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          title="Drag to move"
          className="flex items-center justify-between px-4 pt-3 cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-nav-active">
            <GraduationCap size={14} /> {tour.title}
          </div>
          <button
            data-tutorial-skip
            onClick={() => finish(false)}
            title="Skip tutorial"
            className="p-1 rounded text-page-muted hover:text-page-text hover:bg-page-bg border-0 bg-transparent cursor-pointer"
          >
            <X size={15} />
          </button>
        </div>
        <div className="px-4 pt-2 pb-1">
          <div className="text-[15px] font-semibold mb-1.5">{step.title}</div>
          <div
            className="text-[13px] leading-relaxed text-page-muted space-y-1.5 [&_b]:text-page-text [&_code]:text-[12px] [&_code]:bg-page-bg [&_code]:px-1 [&_code]:rounded"
            dangerouslySetInnerHTML={{ __html: step.body }}
          />
          {!settled && step.target && (
            <div className="text-[11px] text-page-muted mt-2 italic">finding the element…</div>
          )}
          {centered && step.target && (
            <div className="text-[11px] text-page-muted mt-2 italic">
              (that part of the UI isn't visible right now — the explanation still applies)
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 px-3 py-2.5 mt-1 border-t border-page-divider">
          <button
            data-tutorial-back
            onClick={() => go(idx - 1)}
            disabled={idx === 0}
            className="flex items-center gap-1 h-8 px-3 rounded text-xs hover:bg-page-bg disabled:opacity-40 border-0 bg-transparent cursor-pointer text-page-text"
          >
            <ChevronLeft size={14} /> Back
          </button>
          <span className="text-[11px] text-page-muted ml-1">{idx + 1} / {N}</span>
          <button
            data-tutorial-next
            onClick={() => go(idx + 1)}
            className="flex items-center gap-1 h-8 px-4 rounded bg-nav-active text-white text-xs ml-auto border-0 cursor-pointer"
          >
            {idx >= N - 1 ? "Finish" : "Next"} <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
